import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/AuthLayout';
import { Button, Field, Input, useToast } from '@/components/ui';
import { api } from '@/lib/api';
/* Force-change page reached when ``must_change_password`` is true.
 *
 * The user lands here from any post-login navigation; they cannot reach
 * the rest of the app until the temp password is replaced. The Current
 * password field is the system-generated temp value the admin emailed
 * them. New password requirements mirror the backend service
 * (≥10 chars, must differ from current).
 */
export function ForceChangePasswordPage() {
    const toast = useToast();
    const navigate = useNavigate();
    const me = useQuery({
        queryKey: ['me-profile'],
        queryFn: async () => (await api.get('/users/me/')).data,
    });
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const change = useMutation({
        mutationFn: async () => (await api.post('/users/me/change-password/', {
            current_password: current,
            new_password: next,
        })).data,
        onSuccess: () => {
            toast.success('Password updated', 'You can now use Vulcan ERP.');
            // Bumped token version means the current JWT is dead — re-route
            // through the sign-in screen so the auth store picks up a fresh one.
            navigate('/login', { replace: true });
        },
        onError: (err) => toast.error('Could not update', err.response?.data?.detail || err.message),
    });
    return (_jsx(AuthLayout, { title: "Set a new password", subtitle: "Your administrator reset your password. Pick a new one to continue.", children: _jsxs("form", { className: "space-y-4", onSubmit: (e) => {
                e.preventDefault();
                if (next !== confirm) {
                    toast.error('Passwords don\'t match');
                    return;
                }
                change.mutate();
            }, children: [_jsxs("div", { className: "rounded-md border bg-muted/30 px-3 py-2 text-xs", children: ["Signed in as ", _jsx("code", { className: "font-mono", children: me.data?.email })] }), _jsx(Field, { label: "Temporary password (emailed to you)", children: _jsx(Input, { type: "password", autoComplete: "current-password", autoFocus: true, value: current, onChange: (e) => setCurrent(e.target.value) }) }), _jsx(Field, { label: "New password (\u226510 characters)", children: _jsx(Input, { type: "password", autoComplete: "new-password", value: next, onChange: (e) => setNext(e.target.value) }) }), _jsx(Field, { label: "Repeat new password", children: _jsx(Input, { type: "password", autoComplete: "new-password", value: confirm, onChange: (e) => setConfirm(e.target.value) }) }), _jsx(Button, { type: "submit", className: "w-full", loading: change.isPending, disabled: !current || next.length < 10 || next !== confirm, children: "Set password & continue" })] }) }));
}
